import React from 'react';
import { Field, Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { FetchingState } from '../../../contexts/constants';
import { useCurrentProject } from '../../../contexts/settings';
import { useSupplementaryData } from '../../../hooks';
import { useNotifications } from '../../../hooks/useNotifications';
import { usePaths } from '../../../routes/routesHelper';
import { RouteParams } from '../../../routes/types';
import { HorizontalCenterColumnBlock, ShortSettingsBlock } from '../../../styled';
import { FormFooter, HeaderTitle, Notice, Preloader, ToDashboardError } from '../../ui';
import CustomAskField from './CustomAskField';
import { Fields, FormValues } from './CustomAskField/types';
import { FieldSelector } from './FieldSelector';
import { FieldsPreviewFrame } from './FieldsPreviewFrame';
import { StandardFieldLabel } from './StandardFieldLabel';
import { AgeRestrictLink, BlockWrapper, RestrictAgeAlert } from './style';
import { defaultTypes, SUPPLEMENTARY_FIELDS } from './utils';

const SupplementaryInfoForm = ({ history, match }: RouteComponentProps): JSX.Element => {
  const { ageRestrictionsPath, navigationPath, usernameSettingsPath } = usePaths();
  const { projectId } = match.params as RouteParams;
  const intl = useIntl();
  const { fieldOptions, fetchingState, getDataError, updateFields } =
    useSupplementaryData(projectId);
  const { currentUpdateFetching, currentProject } = useCurrentProject();
  const { showErrorNotify, showSuccessNotify } = useNotifications();

  const onSubmit = async (values: FormValues) => {
    try {
      await updateFields(values);

      showSuccessNotify();
    } catch (err) {
      showErrorNotify(err);
    }
  };

  const backToNavigation = () =>
    currentProject?.id && history.push({ pathname: navigationPath(currentProject.id) });

  if (getDataError) {
    return <ToDashboardError error={getDataError} pageName="user-supplementary_open" />;
  }

  if (fetchingState === FetchingState.Loading) {
    return <Preloader />;
  }

  return (
    <Form onSubmit={onSubmit}>
      {({ handleSubmit, pristine, values: { fields, ...rest } }) => {
        return (
          <>
            <HorizontalCenterColumnBlock>
              <HeaderTitle
                title={intl.formatMessage({
                  id: 'user-data-base.settings.supplementary-info.main.title'
                })}
                subtitle={intl.formatMessage({
                  id: 'user-data-base.settings.supplementary-info.main.subtitle'
                })}
                notification={intl.formatMessage({
                  id: 'user-data-base.settings.supplementary-info.main.notification'
                })}
                notificationIcon={Notice}
              />
              <ShortSettingsBlock>
                <FieldSelector fieldsData={fieldOptions} />
                {/* ToDo: uncomment when custom fields will be implemented */}
                {/* <BlockWrapper> */}
                {/*   <Notification palette="brand" icon={Notice}> */}
                {/*     { */}
                {/*       intl.messages[ */}
                {/*         'user-data-base.settings.supplementary-info.default-language.notification' */}
                {/*       ] */}
                {/*     } */}
                {/*   </Notification> */}
                {/* </BlockWrapper> */}
                {(fields as unknown as string[])?.map((fieldName: SUPPLEMENTARY_FIELDS) => {
                  return (
                    <Field key={fieldName} name={fieldName}>
                      {({ input }) => {
                        return (
                          <BlockWrapper>
                            <CustomAskField
                              onChange={input.onChange}
                              value={intl.formatMessage({
                                id: `user-data-base.settings.supplementary-info.field.${fieldName}`
                              })}
                              disabled
                              required={fieldOptions[fieldName]?.is_required}
                              fieldType={fieldOptions[fieldName]?.type || defaultTypes[fieldName]}
                              name={fieldName}
                              label={<StandardFieldLabel name={fieldName} />}
                            />
                            {/* ToDo: uncomment, when age restricting page is added */}
                            {fieldName === 'bday' && (
                              <RestrictAgeAlert>
                                {intl.formatMessage(
                                  { id: 'user-data-base.settings.supplementary-info.age-restrict' },
                                  {
                                    a: (chunk: string) => (
                                      <AgeRestrictLink
                                        target="_blank"
                                        href={
                                          currentProject?.id &&
                                          ageRestrictionsPath(currentProject.id)
                                        }>
                                        {chunk}
                                      </AgeRestrictLink>
                                    )
                                  }
                                )}
                              </RestrictAgeAlert>
                            )}
                            {fieldName === 'username' && (
                              <RestrictAgeAlert>
                                {intl.formatMessage(
                                  { id: 'user-data-base.settings.supplementary-info.age-restrict' },
                                  {
                                    a: (chunk: string) => (
                                      <AgeRestrictLink
                                        target="_blank"
                                        href={
                                          currentProject?.id &&
                                          usernameSettingsPath(currentProject.id)
                                        }>
                                        {chunk}
                                      </AgeRestrictLink>
                                    )
                                  }
                                )}
                              </RestrictAgeAlert>
                            )}
                          </BlockWrapper>
                        );
                      }}
                    </Field>
                  );
                })}
              </ShortSettingsBlock>
              <FieldsPreviewFrame
                projectId={projectId}
                fields={fields as string[]}
                fieldsData={{ ...(rest as Fields) }}
              />
            </HorizontalCenterColumnBlock>

            <FormFooter
              onSubmit={handleSubmit}
              onCancel={backToNavigation}
              fetching={currentUpdateFetching || fetchingState === FetchingState.Updating}
              disabled={pristine}
            />
          </>
        );
      }}
    </Form>
  );
};

export default withRouter(SupplementaryInfoForm);
